import type { ReactNode } from "react";
import { HeroSection } from "./HeroSection";
import { HeroOverline } from "./HeroOverline";
import { cn } from "@/lib/utils";

export interface HeroKpi {
  label: string;
  value: ReactNode;
  unit?: string;
  up?: string;
  sub?: string;
  gold?: boolean;
}

interface HeroProps {
  glyph?: ReactNode;
  overline: ReactNode;
  live?: boolean;
  title: ReactNode;
  miniBadge?: ReactNode;
  sub?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  kpis?: HeroKpi[];
  rightSlot?: ReactNode;
}

export function Hero({
  glyph,
  overline,
  live,
  title,
  miniBadge,
  sub,
  meta,
  actions,
  kpis,
  rightSlot,
}: HeroProps) {
  return (
    <HeroSection padding="comfortable">
      <div className="flex justify-between items-start gap-8 mb-5.5 flex-wrap">
        <div className="flex items-start gap-5 min-w-0">
          {glyph && <HeroGlyph>{glyph}</HeroGlyph>}
          <div className="min-w-0">
            <HeroOverline live={live} className="mb-2">
              {overline}
            </HeroOverline>
            <h1 className="font-brand font-extrabold text-[36px] leading-[1.05] tracking-[-0.025em] text-white m-0 mb-2.5 flex items-center gap-3.5 flex-wrap">
              {title}
              {miniBadge && (
                <span
                  className="font-mono text-[10px] font-semibold px-2.5 py-1 rounded-full border border-white/15 text-white/80 uppercase tracking-[0.08em]"
                  style={{ background: "rgba(255,255,255,0.08)" }}
                >
                  {miniBadge}
                </span>
              )}
            </h1>
            {sub && (
              <div className="text-sm text-white/70 max-w-[640px] leading-[1.5] mb-3">{sub}</div>
            )}
            {meta && <HeroMeta>{meta}</HeroMeta>}
          </div>
        </div>
        {(actions || rightSlot) && (
          <div className="flex gap-2.5 items-center flex-wrap">
            {rightSlot}
            {actions}
          </div>
        )}
      </div>

      {kpis && kpis.length > 0 && (
        <div className="relative z-[2] grid grid-cols-2 md:grid-cols-4 gap-6 pt-5.5 border-t border-white/[0.08]">
          {kpis.map((k) => (
            <HeroKpiCell key={String(k.label)} kpi={k} />
          ))}
        </div>
      )}
    </HeroSection>
  );
}

export function HeroGlyph({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        "w-16 h-16 rounded-[14px] flex items-center justify-center flex-shrink-0 relative overflow-hidden",
      )}
      style={{
        background: "linear-gradient(135deg, rgba(255,200,61,0.2), rgba(255,200,61,0.05))",
        border: "1px solid rgba(255,200,61,0.3)",
      }}
    >
      <span
        className="absolute inset-[-1px] rounded-[14px] pointer-events-none"
        style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,200,61,0.25), transparent 60%)" }}
      />
      <span className="relative text-[#ffc83d] [&_svg]:w-[26px] [&_svg]:h-[26px]">{children}</span>
    </div>
  );
}

export function HeroMeta({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-4 flex-wrap text-xs text-white/55 font-mono tracking-[0.04em]">
      {children}
    </div>
  );
}

export function HeroMetaItem({ k, v }: { k: string; v: ReactNode }) {
  return (
    <span>
      <span className="text-white/40 mr-1">{k}</span>
      <strong className="text-white/90 font-medium">{v}</strong>
    </span>
  );
}

export function HeroMetaSep() {
  return <span className="text-white/25">·</span>;
}

function HeroKpiCell({ kpi }: { kpi: HeroKpi }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-mono text-[10px] text-white/50 tracking-[0.14em] uppercase">{kpi.label}</div>
      <div
        className={cn(
          "font-brand font-extrabold text-[34px] leading-none tracking-[-0.025em] flex items-baseline gap-1.5",
          kpi.gold ? "text-[#ffc83d]" : "text-white",
        )}
      >
        {kpi.value}
        {kpi.unit && <span className="text-sm text-white/40 font-semibold tracking-normal">{kpi.unit}</span>}
        {kpi.up && <span className="text-xs text-[#27a155] font-semibold tracking-normal">{kpi.up}</span>}
      </div>
      {kpi.sub && <div className="text-[11px] text-white/55 font-mono tracking-[0.04em] mt-0.5">{kpi.sub}</div>}
    </div>
  );
}
